"use client"

import { useState, useEffect } from "react"
import {
  Box,
  Typography,
  Paper,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  CircularProgress,
} from "@mui/material"
import { Download as DownloadIcon } from "@mui/icons-material"
import { useDispatch, useSelector } from "react-redux"
import { toast } from "react-toastify"
import moment from "moment"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment"
import { useTheme } from "@mui/material/styles"
import { tokens } from "../../theme"
import { fetchReportSales } from "../../redux/actions/sales"
import { fetchAllShops } from "../../redux/actions/shop"
import jsPDF from "jspdf"
import "jspdf-autotable"
import logo from "../../assets/company.png"

const GenerateReports = () => {
  const dispatch = useDispatch()
  const { shops } = useSelector((state) => state.shop)
  const theme = useTheme()
  const colors = tokens(theme.palette.mode)

  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)
  const [selectedShopIds, setSelectedShopIds] = useState([])
  const [generating, setGenerating] = useState(false)

  useEffect(() => {
    dispatch(fetchAllShops())
  }, [dispatch])

  const allSelected = shops.length > 0 && selectedShopIds.length === shops.length

  const handleShopToggle = (shopId) => {
    setSelectedShopIds((prev) =>
      prev.includes(shopId) ? prev.filter((id) => id !== shopId) : [...prev, shopId]
    )
  }

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedShopIds([])
    } else {
      setSelectedShopIds(shops.map((s) => s._id))
    }
  }

  const canDownload = startDate && endDate && selectedShopIds.length > 0

  const getSelectedShopNames = () => {
    return shops
      .filter((shop) => selectedShopIds.includes(shop._id))
      .map((shop) => shop.name)
  }

  const formatNumber = (num) =>
    num?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ") || "0"

  const generatePDF = async () => {
    if (!canDownload) return

    setGenerating(true)
    try {
      const shopIds = selectedShopIds
      const startStr = moment(startDate).format("YYYY-MM-DD")
      const endStr = moment(endDate).format("YYYY-MM-DD")

      const sales = await dispatch(fetchReportSales(startStr, endStr, shopIds))

      if (!sales || sales.length === 0) {
        toast.warning("Aucune vente trouvée pour les critères sélectionnés")
        setGenerating(false)
        return
      }

      // Flatten sales into rows (one row per product per sale), filtering Bikigiti products only
      const rows = []
      let rowIndex = 1
      sales.forEach((sale) => {
        sale.products.forEach((product) => {
          const productName = product.name || product.product?.name || ""
          if (
            productName.toLowerCase().includes("bikigiti grand format") ||
            productName.toLowerCase().includes("bikigiti petit format")
          ) {
            rows.push({
              index: rowIndex++,
              clientName: sale.client_name || "N/A",
              phone: sale.client_phone || "N/A",
              agency: sale.shop?.name || "N/A",
              product: productName,
              quantity: product.quantity || 0,
              unitPrice: product.prix_de_vente || product.price || 0,
              totalPrice: (product.prix_de_vente || product.price || 0) * (product.quantity || 0),
              paymentMethod: sale.payment_method || "N/A",
              saleDate: moment(sale.createdAt).format("DD/MM/YYYY"),
            })
          }
        })
      })

      if (rows.length === 0) {
        toast.warning("Aucun produit Bikigiti trouvé pour les critères sélectionnés")
        setGenerating(false)
        return
      }

      const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" })
      const pageWidth = doc.internal.pageSize.getWidth()
      const pageHeight = doc.internal.pageSize.getHeight()
      const totalPagesExp = "{total_pages_count_string}"

      // Add logo
      try {
        doc.addImage(logo, "PNG", 14, 10, 20, 20)
      } catch (e) {
        console.error("Error adding logo:", e)
      }

      // Header text
      doc.setFontSize(18)
      doc.setFont("helvetica", "bold")
      doc.text("Rapport de Ventes", 40, 18)

      doc.setFontSize(10)
      doc.setFont("helvetica", "normal")
      doc.text(
        `Du ${moment(startDate).format("DD/MM/YYYY")} au ${moment(endDate).format("DD/MM/YYYY")}`,
        40,
        25
      )
      doc.text(`Agences: ${getSelectedShopNames().join(", ")}`, 40, 31)
      doc.text(
        `Généré le: ${moment().format("DD/MM/YYYY HH:mm")}`,
        pageWidth - 14,
        18,
        { align: "right" }
      )

      // Table
      const tableData = rows.map((row) => [
        row.index,
        row.clientName,
        row.phone,
        row.agency,
        row.product,
        row.quantity,
        formatNumber(row.unitPrice) + " Fbu",
        formatNumber(row.totalPrice) + " Fbu",
        row.paymentMethod,
        row.saleDate,
      ])

      doc.autoTable({
        startY: 38,
        head: [
          [
            "#",
            "Nom du Client",
            "Téléphone",
            "Agence",
            "Produit",
            "Quantité",
            "Prix Unitaire",
            "Prix Total",
            "Mode de Paiement",
            "Date de Vente",
          ],
        ],
        body: tableData,
        styles: {
          fontSize: 8,
          cellPadding: 2,
        },
        headStyles: {
          fillColor: [34, 197, 94],
          textColor: 255,
          fontStyle: "bold",
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245],
        },
        didDrawPage: (data) => {
          // Footer on every page
          const pageCount = doc.internal.getNumberOfPages()
          doc.setFontSize(8)
          doc.setFont("helvetica", "normal")
          doc.text(
            "Système conçu par hyphentech.tech",
            pageWidth / 2,
            pageHeight - 8,
            { align: "center" }
          )
          const pageStr = `Page ${data.pageNumber} / ${totalPagesExp}`
          doc.text(pageStr, pageWidth - 14, pageHeight - 8, { align: "right" })
        },
        margin: { top: 38, bottom: 15 },
      })

      // Summary section on last page
      const finalY = doc.lastAutoTable.finalY + 10

      // Check if we need a new page for summary
      let summaryY = finalY
      if (finalY + 50 > pageHeight - 15) {
        doc.addPage()
        summaryY = 20
      }

      // Calculate summary data
      const grandFormatRows = rows.filter((r) =>
        r.product.toLowerCase().includes("bikigiti grand format")
      )
      const petitFormatRows = rows.filter((r) =>
        r.product.toLowerCase().includes("bikigiti petit format")
      )

      const grandFormatQty = grandFormatRows.reduce((sum, r) => sum + r.quantity, 0)
      const grandFormatRevenue = grandFormatRows.reduce((sum, r) => sum + r.totalPrice, 0)
      const petitFormatQty = petitFormatRows.reduce((sum, r) => sum + r.quantity, 0)
      const petitFormatRevenue = petitFormatRows.reduce((sum, r) => sum + r.totalPrice, 0)
      const grandTotal = grandFormatRevenue + petitFormatRevenue

      doc.setFontSize(12)
      doc.setFont("helvetica", "bold")
      doc.text("Résumé", 14, summaryY)
      summaryY += 8

      doc.setFontSize(9)
      doc.setFont("helvetica", "normal")

      const summaryLines = [
        `Nombre total de ventes: ${rows.length}`,
        `Bikigiti Grand Format: ${grandFormatQty} unités — ${formatNumber(grandFormatRevenue)} Fbu`,
        `Bikigiti Petit Format: ${petitFormatQty} unités — ${formatNumber(petitFormatRevenue)} Fbu`,
      ]

      summaryLines.forEach((line) => {
        doc.text(line, 14, summaryY)
        summaryY += 6
      })

      summaryY += 2
      doc.setFont("helvetica", "bold")
      doc.setFontSize(11)
      doc.text(`Total Général: ${formatNumber(grandTotal)} Fbu`, 14, summaryY)

      // Replace total pages placeholder
      const totalPages = doc.internal.getNumberOfPages()
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i)
        doc.setFontSize(8)
        doc.setFont("helvetica", "normal")
        // Fix the page count in footer
        const pageFooterText = `Page ${i} / ${totalPages}`
        // Overwrite the placeholder
        doc.setFillColor(255, 255, 255)
        doc.rect(pageWidth - 50, pageHeight - 12, 40, 6, "F")
        doc.text(pageFooterText, pageWidth - 14, pageHeight - 8, { align: "right" })

        // Re-add center footer
        doc.text(
          "Système conçu par hyphentech.tech",
          pageWidth / 2,
          pageHeight - 8,
          { align: "center" }
        )
      }

      const fileName = `rapport-ventes_${startStr}_${endStr}.pdf`
      doc.save(fileName)
      toast.success("Rapport PDF généré avec succès")
    } catch (error) {
      console.error("PDF generation error:", error)
      toast.error("Erreur lors de la génération du rapport")
    } finally {
      setGenerating(false)
    }
  }

  return (
    <Box m="20px">
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          mb: 3,
          color: theme.palette.mode === "dark" ? "text.primary" : "white",
        }}
      >
        Générer Rapports de Ventes
      </Typography>

      <Paper
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: theme.palette.background.paper,
          backdropFilter: "blur(10px)",
          border: `1px solid rgba(${colors.primary[600]}, 0.1)`,
          boxShadow: `0 8px 32px rgba(${colors.primary[900]}, 0.15)`,
        }}
      >
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="flex-start">
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              label="Date de début"
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              slotProps={{
                textField: {
                  size: "small",
                  sx: {
                    minWidth: 180,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      "& fieldset": { borderColor: colors.grey[300] },
                      "&:hover fieldset": { borderColor: colors.primary[600] },
                      "&.Mui-focused fieldset": { borderColor: colors.primary[600] },
                    },
                  },
                },
              }}
            />
            <DatePicker
              label="Date de fin"
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              slotProps={{
                textField: {
                  size: "small",
                  sx: {
                    minWidth: 180,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      "& fieldset": { borderColor: colors.grey[300] },
                      "&:hover fieldset": { borderColor: colors.primary[600] },
                      "&.Mui-focused fieldset": { borderColor: colors.primary[600] },
                    },
                  },
                },
              }}
            />
          </LocalizationProvider>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
              Agences
            </Typography>
            <FormGroup row>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allSelected}
                    indeterminate={selectedShopIds.length > 0 && !allSelected}
                    onChange={handleSelectAll}
                    sx={{
                      color: colors.primary[500],
                      "&.Mui-checked": { color: colors.primary[500] },
                    }}
                  />
                }
                label="Tout sélectionner"
              />
              {shops.map((shop) => (
                <FormControlLabel
                  key={shop._id}
                  control={
                    <Checkbox
                      checked={selectedShopIds.includes(shop._id)}
                      onChange={() => handleShopToggle(shop._id)}
                      sx={{
                        color: colors.primary[500],
                        "&.Mui-checked": { color: colors.primary[500] },
                      }}
                    />
                  }
                  label={shop.name}
                />
              ))}
            </FormGroup>
          </Box>
        </Box>

        <Box mt={2}>
          <Button
            variant="contained"
            startIcon={generating ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
            disabled={!canDownload || generating}
            onClick={generatePDF}
            sx={{
              backgroundColor: colors.primary[500],
              "&:hover": { backgroundColor: colors.primary[600] },
              "&:disabled": { opacity: 0.5 },
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              px: 3,
            }}
          >
            {generating ? "Génération en cours..." : "Download PDF Report"}
          </Button>
        </Box>
      </Paper>
    </Box>
  )
}

export default GenerateReports
